import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CgSpinner } from 'react-icons/cg';
import { FaTrashAlt } from 'react-icons/fa';
import { BookingQuestionsModal } from '../common/modals';
import PolarSwitch from '../common/formComponents/polarSwitch';
import PersonSelectorTransfer from '../common/formComponents/PersonSelectorTransfer';
import { SEC_ApiCall } from '../func';
import { toast } from 'react-toastify';
import './bookingstsEditModal.css';

const MAX_EXTERNAL_ATTENDEES = 20;

function buildTimeOptions() {
    const options = [];
    for (let totalMinutes = 0; totalMinutes < (24 * 60); totalMinutes += 5) {
        const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
        const minutes = String(totalMinutes % 60).padStart(2, '0');
        options.push(`${hours}:${minutes}`);
    }
    return options;
}

function parseExternalKey(attendee) {
    const name = String(attendee?.name || '').trim().toLowerCase();
    const email = String(attendee?.email || '').trim().toLowerCase();
    return `${name}|${email}`;
}

function extractInternalAttendeeId(value) {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (value && typeof value === 'object') {
        const candidate = value.cogId || value.id || value.attendeeId || '';
        return String(candidate || '').trim();
    }
    return '';
}

function normalizeInternalAttendeePeople(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    const deduped = new Map();
    values.forEach((item) => {
        const cogId = extractInternalAttendeeId(item);
        if (!cogId || deduped.has(cogId)) {
            return;
        }

        const displayName = String(item?.displayName || item?.name || cogId).trim() || cogId;
        deduped.set(cogId, { cogId, displayName });
    });

    return Array.from(deduped.values());
}

function normalizeName(value) {
    return String(value || '').trim().toLowerCase();
}

/**
 * Modal for editing a BOOKINGSTS booking.
 * Allows title/notes/attendees editing and optional date/time updates after
 * an explicit availability check.
 */
export default function BookingstsEditModal({
    modalActive,
    setModalActive,
    bookingData,
    attendeesAllowed = false,
    onCheckAvailability,
    onSubmit,
}) {
    const [title, setTitle] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attendeesEnabled, setAttendeesEnabled] = useState(false);
    const [attendeesLoading, setAttendeesLoading] = useState(false);
    const [attendeesLoadError, setAttendeesLoadError] = useState('');
    const [allStaffPeople, setAllStaffPeople] = useState([]);
    const [hasLoadedAllStaff, setHasLoadedAllStaff] = useState(false);
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [externalAttendees, setExternalAttendees] = useState([{ id: Date.now(), name: '', email: '' }]);
    const [showNotesToAttendees, setShowNotesToAttendees] = useState(true);
    const [showOtherAttendeesInInvite, setShowOtherAttendeesInInvite] = useState(true);
    const [changeDateTimeEnabled, setChangeDateTimeEnabled] = useState(false);
    const [newDate, setNewDate] = useState('');
    const [newStartTime, setNewStartTime] = useState('');
    const [newEndTime, setNewEndTime] = useState('');
    const [availabilityState, setAvailabilityState] = useState('idle');
    const [removedAttendeeReason, setRemovedAttendeeReason] = useState('');
    const selectedAttendeesRef = useRef([]);

    const timeOptions = useMemo(() => buildTimeOptions(), []);

    const originalInternal = useMemo(() => (
        Array.isArray(bookingData?.attendees?.internal)
            ? bookingData.attendees.internal
                .map((item) => extractInternalAttendeeId(item))
                .filter(Boolean)
            : []
    ), [bookingData?.attendees?.internal]);

    const organizerCogId = useMemo(
        () => String(bookingData?.bookedByCogId || '').trim(),
        [bookingData?.bookedByCogId],
    );
    const selectorExcludedIds = useMemo(
        () => (organizerCogId ? [organizerCogId] : []),
        [organizerCogId],
    );

    const editableOriginalInternal = useMemo(() => (
        originalInternal.filter((attendeeId) => attendeeId !== organizerCogId)
    ), [originalInternal, organizerCogId]);

    const originalExternal = useMemo(() => (
        Array.isArray(bookingData?.attendees?.external)
            ? bookingData.attendees.external
                .map((item) => ({
                    name: String(item?.name || '').trim(),
                    email: String(item?.email || '').trim(),
                }))
                .filter((item) => item.name && item.email)
            : []
    ), [bookingData?.attendees?.external]);

    const currentAttendeeNames = useMemo(() => {
        const names = [];
        const seen = new Set();
        const blockedNames = new Set([
            normalizeName(bookingData?.bookedByDisplayName),
            normalizeName(bookingData?.bookedForDisplayName),
        ]);

        const internalPeople = normalizeInternalAttendeePeople(bookingData?.attendeeInternalPeople)
            .filter((person) => String(person?.cogId || '').trim() !== organizerCogId);
        internalPeople.forEach((person) => {
            const name = String(person?.displayName || '').trim();
            const key = normalizeName(name);
            if (!key || blockedNames.has(key) || seen.has(key)) {
                return;
            }
            seen.add(key);
            names.push(name);
        });

        originalExternal.forEach((person) => {
            const name = String(person?.name || '').trim();
            const key = normalizeName(name);
            if (!key || blockedNames.has(key) || seen.has(key)) {
                return;
            }
            seen.add(key);
            names.push(name);
        });

        if (names.length > 0) {
            return names;
        }

        const fallbackNames = Array.isArray(bookingData?.attendeeDisplayNames) ? bookingData.attendeeDisplayNames : [];
        fallbackNames.forEach((value) => {
            const name = String(value || '').trim();
            const key = normalizeName(name);
            if (!key || blockedNames.has(key) || seen.has(key)) {
                return;
            }
            seen.add(key);
            names.push(name);
        });

        return names;
    }, [
        bookingData?.attendeeInternalPeople,
        bookingData?.attendeeDisplayNames,
        bookingData?.bookedByDisplayName,
        bookingData?.bookedForDisplayName,
        organizerCogId,
        originalExternal,
    ]);

    const removedAttendeesCount = useMemo(() => {
        if (!attendeesEnabled) {
            return 0;
        }

        const selectedInternalIds = new Set(
            selectedAttendees
                .map((item) => String(item?.cogId || '').trim())
                .filter(Boolean),
        );
        const removedInternal = editableOriginalInternal.filter((attendeeId) => !selectedInternalIds.has(attendeeId));

        const cleanExternal = externalAttendees
            .map((row) => ({
                name: String(row?.name || '').trim(),
                email: String(row?.email || '').trim(),
            }))
            .filter((row) => row.name && row.email);
        const externalKeys = new Set(cleanExternal.map((row) => parseExternalKey(row)));
        const removedExternal = originalExternal.filter((attendee) => !externalKeys.has(parseExternalKey(attendee)));

        return removedInternal.length + removedExternal.length;
    }, [attendeesEnabled, selectedAttendees, externalAttendees, editableOriginalInternal, originalExternal]);

    useEffect(() => {
        selectedAttendeesRef.current = selectedAttendees;
    }, [selectedAttendees]);

    useEffect(() => {
        if (!modalActive) {
            setSubmitting(false);
            setAvailabilityState('idle');
            setRemovedAttendeeReason('');
            return;
        }

        const bookingTitle = String(bookingData?.title || '').trim();
        const bookingNotes = String(bookingData?.notes || '').trim();
        const bookingDate = String(bookingData?.date || '').trim();
        const bookingStart = String(bookingData?.startTime || '').trim();
        const bookingEnd = String(bookingData?.endTime || '').trim();
        const bookingAttendees = bookingData?.attendees || {};
        const inviteOptions = bookingData?.attendeeInviteOptions || {};

        setTitle(bookingTitle);
        setNotes(bookingNotes);
        setNewDate(bookingDate);
        setNewStartTime(bookingStart);
        setNewEndTime(bookingEnd);
        setChangeDateTimeEnabled(false);
        setAvailabilityState('idle');
        setRemovedAttendeeReason('');

        const externalRows = Array.isArray(bookingAttendees?.external)
            ? bookingAttendees.external
                .map((item, index) => ({
                    id: Date.now() + index,
                    name: String(item?.name || '').trim(),
                    email: String(item?.email || '').trim(),
                }))
                .filter((item) => item.name && item.email)
            : [];

        const internalPeopleFromBooking = normalizeInternalAttendeePeople(bookingData?.attendeeInternalPeople);
        const internalAttendees = internalPeopleFromBooking.length
            ? internalPeopleFromBooking.filter((person) => person.cogId !== organizerCogId)
            : editableOriginalInternal.map((cogId) => ({ cogId, displayName: cogId }));

        // Keep attendees section collapsed by default in edit mode and preload
        // only current attendees; full staff list is loaded when toggle is enabled.
        setAttendeesEnabled(false);
        setAttendeesLoading(false);
        setAttendeesLoadError('');
        setHasLoadedAllStaff(false);
        setSelectedAttendees(internalAttendees);
        setAllStaffPeople(internalAttendees);
        setExternalAttendees(externalRows.length ? externalRows : [{ id: Date.now(), name: '', email: '' }]);
        setShowNotesToAttendees(!Boolean(inviteOptions?.omitNotesInInvite));
        setShowOtherAttendeesInInvite(!Boolean(inviteOptions?.omitOtherAttendeesInInvite));
    }, [modalActive, bookingData, editableOriginalInternal, organizerCogId]);

    useEffect(() => {
        if (!attendeesAllowed) {
            setAttendeesEnabled(false);
        }
    }, [attendeesAllowed]);

    useEffect(() => {
        if (!modalActive || !attendeesAllowed || !attendeesEnabled || hasLoadedAllStaff || attendeesLoading) {
            return;
        }
        setAttendeesLoading(true);
        setAttendeesLoadError('');
        (async () => {
            try {
                const response = await SEC_ApiCall('bookingsts/staff', {});
                const people = (Array.isArray(response?.items) ? response.items : [])
                    .filter((item) => String(item?.cogId || '').trim() !== organizerCogId);
                const lookup = new Map(people.map((item) => [String(item?.cogId || '').trim(), item]));
                const currentSelected = selectedAttendeesRef.current || [];
                let hydratedSelected = currentSelected.map((selected) => {
                    const attendeeId = String(selected?.cogId || '').trim();
                    return lookup.get(attendeeId) || { cogId: attendeeId, displayName: attendeeId };
                }).filter((selected) => String(selected?.cogId || '').trim() !== organizerCogId);

                if (hydratedSelected.length === 0) {
                    const bookingAttendeeNames = Array.isArray(bookingData?.attendeeDisplayNames)
                        ? bookingData.attendeeDisplayNames
                        : [];
                    const excludedNames = new Set([
                        normalizeName(bookingData?.bookedByDisplayName),
                        normalizeName(bookingData?.bookedForDisplayName),
                    ]);
                    const targetNameSet = new Set(
                        bookingAttendeeNames
                            .map((name) => normalizeName(name))
                            .filter((name) => name && !excludedNames.has(name)),
                    );

                    if (targetNameSet.size > 0) {
                        hydratedSelected = people.filter((person) => targetNameSet.has(normalizeName(person?.displayName)));
                    }
                }

                const knownIds = new Set(people.map((item) => String(item?.cogId || '').trim()).filter(Boolean));
                const missingSelected = hydratedSelected.filter((item) => !knownIds.has(String(item?.cogId || '').trim()));

                setSelectedAttendees(hydratedSelected);
                setAllStaffPeople([...people, ...missingSelected]);
                setHasLoadedAllStaff(true);
            } catch (error) {
                console.error('failed to load attendees list', error);
                toast.error('Failed to load attendees list. Please try again.', { autoClose: 4000, position: 'top-right' });
                setAttendeesLoadError('Failed to load staff list.');
            } finally {
                setAttendeesLoading(false);
            }
        })();
    }, [
        modalActive,
        attendeesAllowed,
        attendeesEnabled,
        hasLoadedAllStaff,
        attendeesLoading,
        organizerCogId,
        bookingData?.attendeeDisplayNames,
        bookingData?.bookedByDisplayName,
        bookingData?.bookedForDisplayName,
    ]);

    useEffect(() => {
        if (!changeDateTimeEnabled) {
            setAvailabilityState('idle');
            return;
        }
        setAvailabilityState('idle');
    }, [changeDateTimeEnabled, newDate, newStartTime, newEndTime]);

    function handleExternalAttendeeChange(id, field, value) {
        setExternalAttendees((previousRows) => previousRows.map((row) => {
            if (row.id !== id) {
                return row;
            }
            return { ...row, [field]: value };
        }));
    }

    function handleAddExternalAttendee() {
        if (externalAttendees.length >= MAX_EXTERNAL_ATTENDEES) {
            toast.error(`Only ${MAX_EXTERNAL_ATTENDEES} external attendees are allowed.`, { autoClose: 4000, position: 'top-right' });
            return;
        }
        setExternalAttendees((previousRows) => [
            ...previousRows,
            { id: Date.now() + Math.floor(Math.random() * 1000), name: '', email: '' },
        ]);
    }

    function handleRemoveExternalAttendee(id) {
        setExternalAttendees((previousRows) => {
            const filteredRows = previousRows.filter((row) => row.id !== id);
            if (!filteredRows.length) {
                return [{ id: Date.now(), name: '', email: '' }];
            }
            return filteredRows;
        });
    }

    function getCleanExternalAttendees() {
        const cleanedRows = externalAttendees
            .map((row) => ({
                name: String(row?.name || '').trim(),
                email: String(row?.email || '').trim(),
            }))
            .filter((row) => row.name || row.email);

        const validRows = [];
        const seen = new Set();
        for (const row of cleanedRows) {
            if (!row.name || !row.email) {
                throw new Error('Each external attendee requires both a name and an email address.');
            }
            const dedupeKey = parseExternalKey(row);
            if (seen.has(dedupeKey)) {
                continue;
            }
            seen.add(dedupeKey);
            validRows.push(row);
        }
        return validRows;
    }

    async function handleCheckAvailability() {
        if (!changeDateTimeEnabled) {
            return;
        }
        if (!newDate || !newStartTime || !newEndTime) {
            toast.error('Please select date, start time and end time first.', { autoClose: 4000, position: 'top-right' });
            return;
        }
        if (newEndTime <= newStartTime) {
            toast.error('End time must be later than start time.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        setAvailabilityState('checking');
        try {
            const response = await onCheckAvailability({
                date: newDate,
                startTime: newStartTime,
                endTime: newEndTime,
            });
            if (Boolean(response?.available)) {
                setAvailabilityState('available');
                toast.success('Room is available for the selected slot.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            setAvailabilityState('unavailable');
            toast.error(String(response?.message || 'Room is not available for the selected slot.'), { autoClose: 4000, position: 'top-right' });
        } catch (error) {
            console.error('availability check failed', error);
            setAvailabilityState('error');
            toast.error('Availability check failed. Please try again.', { autoClose: 4000, position: 'top-right' });
        }
    }

    async function handleSave() {
        if (changeDateTimeEnabled) {
            if (!newDate || !newStartTime || !newEndTime) {
                toast.error('Please select date, start time and end time.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            if (newEndTime <= newStartTime) {
                toast.error('End time must be later than start time.', { autoClose: 4000, position: 'top-right' });
                return;
            }
            if (availabilityState !== 'available') {
                toast.error('Please check availability before saving.', { autoClose: 4000, position: 'top-right' });
                return;
            }
        }

        if (removedAttendeesCount > 0 && !String(removedAttendeeReason || '').trim()) {
            toast.error('Please provide a reason for removed attendee cancellation emails.', { autoClose: 4000, position: 'top-right' });
            return;
        }

        setSubmitting(true);
        try {
            const external = attendeesEnabled ? getCleanExternalAttendees() : [...originalExternal];
            const internal = attendeesEnabled
                ? Array.from(new Set(
                    selectedAttendees
                        .map((attendee) => String(attendee?.cogId || '').trim())
                        .filter((attendeeId) => Boolean(attendeeId) && attendeeId !== organizerCogId),
                ))
                : [...originalInternal];

            await onSubmit({
                title: String(title || '').trim(),
                notes: String(notes || '').trim(),
                attendees: { internal, external },
                omitNotesInInvite: !showNotesToAttendees,
                omitOtherAttendeesInInvite: !showOtherAttendeesInInvite,
                changeDateTime: Boolean(changeDateTimeEnabled),
                newDate: String(newDate || '').trim(),
                newStartTime: String(newStartTime || '').trim(),
                newEndTime: String(newEndTime || '').trim(),
                removedAttendeeReason: String(removedAttendeeReason || '').trim(),
            });
            setModalActive(false);
        } catch (error) {
            if (String(error?.message || '').includes('external attendee')) {
                toast.error(error.message, { autoClose: 4000, position: 'top-right' });
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <BookingQuestionsModal modalActiveState={modalActive} setStateFunction={setModalActive}>
            <div className='bookingstsEditModalContainer'>
                <div className='bookingstsEditModalDetails'>
                    <p>{String(bookingData?.roomName || '')}</p>
                    <p>{String(bookingData?.date || '')} {String(bookingData?.startTime || '')} - {String(bookingData?.endTime || '')}</p>
                </div>

                <div className='bookingstsEditModalField'>
                    <label htmlFor='bookingstsEditTitle'>Title (optional)</label>
                    <input
                        id='bookingstsEditTitle'
                        type='text'
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder='Meeting title'
                    />
                </div>

                <div className='bookingstsEditModalField'>
                    <label htmlFor='bookingstsEditNotes'>Notes (optional)</label>
                    <textarea
                        id='bookingstsEditNotes'
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder='Additional notes'
                    />
                </div>

                {attendeesAllowed && (
                    <>
                        <div className='bookingstsEditToggleRow'>
                            <span className='bookingstsEditToggleLabel'>Edit Attendees</span>
                            <PolarSwitch
                                data={attendeesEnabled}
                                setData={setAttendeesEnabled}
                                loading={attendeesLoading}
                            />
                        </div>
                        <div className='bookingstsEditCurrentAttendees'>
                            <span>Current attendees:</span> {currentAttendeeNames.length ? currentAttendeeNames.join(', ') : 'None'}
                        </div>

                        {attendeesEnabled && (
                            <>
                                <div className='bookingstsEditPrivacyOptions'>
                                    <div className='bookingstsEditToggleRow'>
                                        <span className='bookingstsEditToggleLabel'>Show notes to attendees</span>
                                        <PolarSwitch
                                            data={showNotesToAttendees}
                                            setData={setShowNotesToAttendees}
                                            loading={false}
                                        />
                                    </div>
                                    <div className='bookingstsEditToggleRow'>
                                        <span className='bookingstsEditToggleLabel'>Show other attendees in email invite</span>
                                        <PolarSwitch
                                            data={showOtherAttendeesInInvite}
                                            setData={setShowOtherAttendeesInInvite}
                                            loading={false}
                                        />
                                    </div>
                                </div>

                                {attendeesLoading ? (
                                    <div className='bookingstsEditStaffSelectorStatus'>Loading staff</div>
                                ) : attendeesLoadError ? (
                                    <div className='bookingstsEditStaffSelectorStatus bookingstsEditStaffSelectorStatusError'>
                                        {attendeesLoadError}
                                    </div>
                                ) : (
                                    <PersonSelectorTransfer
                                        allPeople={allStaffPeople}
                                        nameKey='displayName'
                                        idKey='cogId'
                                        selectedPeople={selectedAttendees}
                                        excludedIds={selectorExcludedIds}
                                        onChange={setSelectedAttendees}
                                        allTitle='All staff'
                                        selectedTitle='Selected internal attendees'
                                        enableLeftSearch
                                        leftSearchPlaceholder='Search staff'
                                        disabled={false}
                                    />
                                )}

                                <div className='bookingstsEditExternalWrap'>
                                    <label className='bookingstsEditExternalLabel'>External Attendees</label>
                                    {externalAttendees.map((externalAttendee) => (
                                        <div className='bookingstsEditExternalRow' key={externalAttendee.id}>
                                            <input
                                                type='text'
                                                value={externalAttendee.name}
                                                onChange={(event) => handleExternalAttendeeChange(externalAttendee.id, 'name', event.target.value)}
                                                placeholder='Person name'
                                            />
                                            <input
                                                type='email'
                                                value={externalAttendee.email}
                                                onChange={(event) => handleExternalAttendeeChange(externalAttendee.id, 'email', event.target.value)}
                                                placeholder='Person email'
                                            />
                                            <button
                                                type='button'
                                                className='bookingstsEditExternalRemove'
                                                onClick={() => handleRemoveExternalAttendee(externalAttendee.id)}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type='button'
                                        className='bookingstsEditExternalAdd'
                                        onClick={handleAddExternalAttendee}
                                    >
                                        + Add external attendee ({externalAttendees.length}/{MAX_EXTERNAL_ATTENDEES})
                                    </button>
                                </div>

                                {removedAttendeesCount > 0 && (
                                    <div className='bookingstsEditModalField'>
                                        <label htmlFor='bookingstsRemovedReason'>
                                            Cancellation reason for removed attendees
                                        </label>
                                        <textarea
                                            id='bookingstsRemovedReason'
                                            value={removedAttendeeReason}
                                            onChange={(event) => setRemovedAttendeeReason(event.target.value)}
                                            placeholder='Reason shown in removal emails'
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}

                <div className='bookingstsEditToggleRow'>
                    <span className='bookingstsEditToggleLabel'>Change date/time</span>
                    <PolarSwitch
                        data={changeDateTimeEnabled}
                        setData={setChangeDateTimeEnabled}
                        loading={false}
                    />
                </div>

                {changeDateTimeEnabled && (
                    <div className='bookingstsEditDateTimeSection'>
                        <div className='bookingstsEditDateRow'>
                            <label htmlFor='bookingstsEditDate'>Date</label>
                            <input
                                id='bookingstsEditDate'
                                type='date'
                                value={newDate}
                                onChange={(event) => setNewDate(event.target.value)}
                            />
                        </div>

                        <div className='bookingstsEditTimeRow'>
                            <div>
                                <label htmlFor='bookingstsEditStartTime'>Start time</label>
                                <select
                                    id='bookingstsEditStartTime'
                                    value={newStartTime}
                                    onChange={(event) => setNewStartTime(event.target.value)}
                                >
                                    {timeOptions.map((timeValue) => (
                                        <option key={timeValue} value={timeValue}>{timeValue}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor='bookingstsEditEndTime'>End time</label>
                                <select
                                    id='bookingstsEditEndTime'
                                    value={newEndTime}
                                    onChange={(event) => setNewEndTime(event.target.value)}
                                >
                                    {timeOptions.map((timeValue) => (
                                        <option key={timeValue} value={timeValue}>{timeValue}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                type='button'
                                className='bookingstsEditCheckButton'
                                onClick={handleCheckAvailability}
                                disabled={availabilityState === 'checking'}
                            >
                                {availabilityState === 'checking' ? <CgSpinner className='submit-button-spinner' /> : 'Check availability'}
                            </button>
                        </div>
                        {availabilityState === 'available' && (
                            <div className='bookingstsEditAvailability bookingstsEditAvailabilityOk'>
                                Availability confirmed.
                            </div>
                        )}
                        {(availabilityState === 'unavailable' || availabilityState === 'error') && (
                            <div className='bookingstsEditAvailability bookingstsEditAvailabilityError'>
                                Availability not confirmed.
                            </div>
                        )}
                    </div>
                )}

                <div className='bookingstsEditSubmitArea'>
                    <button
                        className='formSubmitButton'
                        type='button'
                        onClick={handleSave}
                        disabled={submitting || (changeDateTimeEnabled && availabilityState !== 'available')}
                    >
                        {submitting ? <CgSpinner className='submit-button-spinner' /> : 'Save changes'}
                    </button>
                </div>
            </div>
        </BookingQuestionsModal>
    );
}
